import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { prazosDaCondicao, TIPOS_FORMA, type CondicaoPagamento, type FormaPagamento } from './tipos-catalogos';
import { rotuloForma } from '../../nucleo/forma-pagamento';

/** Botões da aba de catálogo: baixar modelo e importar (com `cadastros.importar`) e o novo registro. */
function AcoesCatalogo({ caminho, entidade, rotuloNovo, aoNovo, aoImportar }: { caminho: string; entidade: string; rotuloNovo: string; aoNovo: () => void; aoImportar: () => void }) {
  const { t } = useTranslation('cadastros');
  const s = useSessao();
  if (!s.pode('cadastros.criar')) return null;
  return (
    <AcaoPrimaria>
      {s.pode('cadastros.excluir') && <BotaoLixeira entidades={[entidade]} />}
      {s.pode('cadastros.importar') && (
        <>
          <BotaoModeloCsv caminho={caminho} />
          <Botao icone={<Upload className="size-4" />} onClick={aoImportar}>
            {t('acoes.importar')}
          </Botao>
        </>
      )}
      <Botao variante="primario" icone={<Plus className="size-4" />} onClick={aoNovo}>
        {rotuloNovo}
      </Botao>
    </AcaoPrimaria>
  );
}

const CAMINHO_FORMAS = '/cadastros/formas-pagamento';

/**
 * Fase 2 (W6, L13): formas de pagamento (o meio). Lote 09/10 (B4): sem o campo Nome, como no antigo; o nome é o tipo mais a
 * abreviatura ("Pix Sicredi"), e a abreviatura distingue formas do mesmo tipo.
 */
export function FormasPagamento() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<FormaPagamento | 'nova' | null>(null);
  const [importar, setImportar] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const atual = aberto && aberto !== 'nova' ? aberto : null;
  const podeEditar = atual ? s.pode('cadastros.editar') : s.pode('cadastros.criar');
  const excluir = useMutation({
    mutationFn: () => del(`${CAMINHO_FORMAS}/${atual!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CAMINHO_FORMAS] });
      setExcluindo(false);
      setAberto(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => (setExcluindo(false), toast.erro(mensagemDeErro(e))),
  });
  const campos: DefCampo[] = [
    { nome: 'tipo', rotulo: t('formas.campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: TIPOS_FORMA.map((x) => ({ valor: x, rotulo: t(`formas.tipos.${x}`) })), desabilitado: !podeEditar },
    { nome: 'abreviatura', rotulo: t('formas.campos.abreviatura'), tipo: 'texto', largura: 'meia', max: 20, ajuda: t('formas.ajudaAbreviatura'), desabilitado: !podeEditar },
    { nome: 'ativa', rotulo: t('formas.campos.ativa'), tipo: 'booleano', largura: 'meia', desabilitado: !podeEditar },
    { nome: 'descricao', rotulo: t('formas.campos.descricao'), tipo: 'area', desabilitado: !podeEditar },
  ];
  const colunas: Coluna<FormaPagamento>[] = [
    // Lote 09/10 (B4): como no antigo, Tipo, Abreviatura, Descrição e Ativa.
    { chave: 'tipo', rotulo: t('formas.campos.tipo'), principal: true, render: (x) => <span className="font-medium">{t(`formas.tipos.${x.tipo}`)}</span> },
    { chave: 'abreviatura', rotulo: t('formas.campos.abreviatura'), render: (x) => textoLivre(x.abreviatura ?? '') },
    { chave: 'descricao', rotulo: t('formas.campos.descricao'), render: (x) => textoLivre(x.descricao ?? '') },
    { chave: 'ativa', rotulo: t('formas.campos.ativa'), render: (x) => (x.ativa ? tn('comum.sim') : tn('comum.nao')) },
  ];
  return (
    <>
      <AcoesCatalogo caminho={CAMINHO_FORMAS} entidade="cadastros.forma_pagamento" rotuloNovo={t('formas.nova')} aoNovo={() => setAberto('nova')} aoImportar={() => setImportar(true)} />
      <Tabela<FormaPagamento> tela="cadastros.formas_pagamento" caminho={CAMINHO_FORMAS} colunas={colunas} entidade="cadastros.forma_pagamento" aoClicar={(x) => setAberto(x)} vazio={<Vazio texto={t('formas.vazio')} />} />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? textoLivre(rotuloForma(atual)) : t('formas.nova')}
        rodape={
          <>
            {atual && s.pode('cadastros.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setExcluindo(true)}>
                {t('servicos.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-forma-pagamento">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'nova'}
          id="form-forma-pagamento"
          campos={campos}
          entidade="cadastros.forma_pagamento"
          inicial={(atual ?? { tipo: 'pix', ativa: true }) as unknown as Record<string, unknown>}
          aoEnviar={async (v) => {
            // Fase 2 (W19, K5): campos personalizados da forma de pagamento.
            const corpo = { tipo: v.tipo, abreviatura: (v.abreviatura as string) || null, descricao: (v.descricao as string) || null, ativa: v.ativa !== false, extras: v.extras ?? {} };
            if (atual) await patch(`${CAMINHO_FORMAS}/${atual.id}`, corpo);
            else await post(CAMINHO_FORMAS, corpo);
            await qc.invalidateQueries({ queryKey: [CAMINHO_FORMAS] });
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} perigoso titulo={t('formas.excluirTitulo')} texto={t('formas.excluirTexto')} />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho={CAMINHO_FORMAS} titulo={t('formas.importar')} />
    </>
  );
}

const CAMINHO_CONDICOES = '/cadastros/condicoes-pagamento';

/** Prazos da condição no idioma: "À vista" ou "30 dias, 60 dias, 90 dias". */
function Prazos({ codigo }: { codigo: string }) {
  const { t } = useTranslation('cadastros');
  const dias = prazosDaCondicao(codigo);
  return <>{codigo === 'avista' ? t('condicoes.avista') : dias.map((d) => t('condicoes.dias', { count: d })).join(', ')}</>;
}

/** Fase 2 (W6, L13): condições de pagamento (o prazo) com nome; o código é o texto que Contas e Compras já gravam. */
export function CondicoesPagamento() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<CondicaoPagamento | 'nova' | null>(null);
  const [importar, setImportar] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const atual = aberto && aberto !== 'nova' ? aberto : null;
  const podeEditar = atual ? s.pode('cadastros.editar') : s.pode('cadastros.criar');
  const excluir = useMutation({
    mutationFn: () => del(`${CAMINHO_CONDICOES}/${atual!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CAMINHO_CONDICOES] });
      setExcluindo(false);
      setAberto(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => (setExcluindo(false), toast.erro(mensagemDeErro(e))),
  });
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('condicoes.campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia', desabilitado: !podeEditar },
    { nome: 'codigo', rotulo: t('condicoes.campos.codigo'), tipo: 'texto', obrigatorio: true, largura: 'meia', ajuda: t('condicoes.ajudaCodigo'), desabilitado: !podeEditar },
    { nome: 'ativa', rotulo: t('condicoes.campos.ativa'), tipo: 'booleano', desabilitado: !podeEditar },
  ];
  const colunas: Coluna<CondicaoPagamento>[] = [
    { chave: 'nome', rotulo: t('condicoes.campos.nome'), principal: true, render: (x) => <span className="font-medium">{textoLivre(x.nome)}</span> },
    { chave: 'codigo', rotulo: t('condicoes.campos.codigo'), mono: true, render: (x) => x.codigo },
    { chave: 'prazos', rotulo: t('condicoes.campos.prazos'), render: (x) => <Prazos codigo={x.codigo} /> },
    { chave: 'ativa', rotulo: t('condicoes.campos.ativa'), render: (x) => (x.ativa ? tn('comum.sim') : tn('comum.nao')) },
  ];
  return (
    <>
      <AcoesCatalogo caminho={CAMINHO_CONDICOES} entidade="cadastros.condicao_pagamento" rotuloNovo={t('condicoes.nova')} aoNovo={() => setAberto('nova')} aoImportar={() => setImportar(true)} />
      <Tabela<CondicaoPagamento> tela="cadastros.condicoes_pagamento" caminho={CAMINHO_CONDICOES} colunas={colunas} entidade="cadastros.condicao_pagamento" aoClicar={(x) => setAberto(x)} vazio={<Vazio texto={t('condicoes.vazio')} />} />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? textoLivre(atual.nome) : t('condicoes.nova')}
        rodape={
          <>
            {atual && s.pode('cadastros.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setExcluindo(true)}>
                {t('servicos.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-condicao-pagamento">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'nova'}
          id="form-condicao-pagamento"
          campos={campos}
          entidade="cadastros.condicao_pagamento"
          inicial={(atual ?? { ativa: true }) as unknown as Record<string, unknown>}
          aoEnviar={async (v) => {
            const corpo = { nome: v.nome, codigo: v.codigo, ativa: v.ativa !== false, extras: v.extras ?? {} };
            if (atual) await patch(`${CAMINHO_CONDICOES}/${atual.id}`, corpo);
            else await post(CAMINHO_CONDICOES, corpo);
            await qc.invalidateQueries({ queryKey: [CAMINHO_CONDICOES] });
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} perigoso titulo={t('condicoes.excluirTitulo')} texto={t('condicoes.excluirTexto')} />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho={CAMINHO_CONDICOES} titulo={t('condicoes.importar')} />
    </>
  );
}
